// Modelo del dashboard "Brújula de cumplimiento" (docs/sdd/02-spec-dashboard.md).
// Módulo PURO: sin Vue, sin DOM, sin red. Recibe la respuesta de
// POST reclassification_scores/puntation/scores (campos viejos + phva/standards/items/history)
// y devuelve un modelo de vista normalizado. Si faltan los campos nuevos (backend sin
// desplegar) degrada: las listas quedan vacías y los flags `has*` en false.

export const THRESHOLDS = { critical: 60, acceptable: 85 }; // Res. 0312/2019

export const CYCLES = [
  { key: "planear", label: "Planear", standards: [1, 2] },
  { key: "hacer", label: "Hacer", standards: [3, 4, 5] },
  { key: "verificar", label: "Verificar", standards: [6] },
  { key: "actuar", label: "Actuar", standards: [7] },
];

export const STATUSES = ["Aprobado", "Pendiente", "No cumplido", "Sin iniciar"];

// company_classification_id => grupo de estándares mínimos (Res. 0312/2019)
export const CLASSIFICATIONS = {
  1: { short: "UPA", long: "UPA · hasta 10 trabajadores · riesgo I" },
  2: { short: "Grupo 1", long: "Grupo 1 · 1 a 10 trabajadores · riesgo I, II o III" },
  3: { short: "Grupo 2", long: "Grupo 2 · 11 a 50 trabajadores · riesgo I, II o III" },
  4: { short: "Grupo 3", long: "Grupo 3 · más de 50 trabajadores o riesgo IV o V" },
};

const MONTHS_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const MONTHS_LONG = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// ---------- formato (es-CO: coma decimal, punto de miles; sin Intl para ser testeable en node) ----------
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const isNil = (v) => v === null || v === undefined || v === "";

export function fmtNum(v, max = 2) {
  const n = Math.round(num(v) * Math.pow(10, max)) / Math.pow(10, max);
  const [int, dec] = String(n).split(".");
  const sign = int.startsWith("-") ? "-" : "";
  const grouped = int.replace("-", "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return sign + grouped + (dec ? "," + dec : "");
}
export const fmtPct = (v, max = 2) => `${fmtNum(v, max)} %`;
export const fmtPts = (v) => `${fmtNum(v)} pts`;

export function monthLabel(ym, long = false) {
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return String(ym);
  return long ? `${MONTHS_LONG[m - 1]} de ${y}` : MONTHS_SHORT[m - 1];
}

// ---------- semáforo (umbrales exactos de la Res. 0312) ----------
// crítico < 60 · moderadamente aceptable 60–85 (ambos incluidos) · aceptable > 85
export function semaphore(pct) {
  if (isNil(pct) || !Number.isFinite(Number(pct))) return null;
  const p = Number(pct);
  if (p < THRESHOLDS.critical) return { key: "critical", label: "Crítico", icon: "alert" };
  if (p <= THRESHOLDS.acceptable) {
    return { key: "warning", label: "Moderadamente aceptable", icon: "dash" };
  }
  return { key: "good", label: "Aceptable", icon: "check" };
}

// ---------- utilidades ----------
const seqParts = (s) => String(s).split(".").map((x) => parseInt(x, 10) || 0);
export function compareSequence(a, b) {
  const pa = seqParts(a);
  const pb = seqParts(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
}

const pctOf = (approved, required) => (num(required) > 0 ? (num(approved) / num(required)) * 100 : null);
const arr = (v) => (Array.isArray(v) ? v : []);

// ---------- normalización ----------
export function normalize(raw) {
  const d = raw && typeof raw === "object" ? raw : {};
  const classificationId = isNil(d.company_classification_id) ? null : Number(d.company_classification_id);
  const group = CLASSIFICATIONS[classificationId] || null;
  const classified = !!group;

  const requiredScore = num(d.required_score);
  const approvedScore = num(d.approved_score);
  const totalPct = classified
    ? !isNil(d.progress_percentage) ? num(d.progress_percentage) : pctOf(approvedScore, requiredScore)
    : null;

  const total = {
    pct: totalPct,
    requiredScore,
    approvedScore,
    requiredItems: num(d.required_items),
    approvedItems: num(d.approved_items),
    status: semaphore(totalPct),
  };

  // --- PHVA ---
  const phvaRaw = arr(d.phva);
  const phva = CYCLES.map((c) => {
    const p = phvaRaw.find((x) => x && x.key === c.key);
    if (!p) return null;
    const rs = num(p.required_score);
    const as = num(p.approved_score);
    const pct = rs > 0 ? (isNil(p.percentage) ? pctOf(as, rs) : num(p.percentage)) : null;
    return {
      key: c.key,
      label: p.label || c.label,
      requiredItems: num(p.required_items),
      approvedItems: num(p.approved_items),
      requiredScore: rs,
      approvedScore: as,
      pct, // null = la fase no aplica a este grupo (0 puntos exigidos)
      applicable: rs > 0,
      status: semaphore(pct),
    };
  }).filter(Boolean);

  // --- Estándares ---
  const standards = arr(d.standards)
    .map((s) => {
      const rs = num(s.required_score);
      const as = num(s.approved_score);
      return {
        number: num(s.number),
        name: s.name || `Estándar ${s.number}`,
        cycle: s.cycle,
        requiredItems: num(s.required_items),
        approvedItems: num(s.approved_items),
        requiredScore: rs,
        approvedScore: as,
        pct: rs > 0 ? (isNil(s.percentage) ? pctOf(as, rs) : num(s.percentage)) : null,
      };
    })
    .sort((a, b) => a.number - b.number);

  // --- Ítems (requeridos por norma + complementarios; se separan aquí) ---
  const itemsAll = arr(d.items).map((i) => ({
    id: i.id,
    sequence: String(i.sequence),
    description: i.description || "",
    standard: num(i.standard),
    cycle: i.cycle,
    value: num(i.value),
    required: i.required !== false,
    status: STATUSES.includes(i.status) ? i.status : "Sin iniciar",
  }));
  const items = itemsAll.filter((i) => i.required).sort((a, b) => compareSequence(a.sequence, b.sequence));
  const complementaryItems = itemsAll.filter((i) => !i.required);

  const statusCounts = STATUSES.reduce((acc, s) => {
    acc[s] = items.filter((i) => i.status === s).length;
    return acc;
  }, {});

  // Siguientes acciones: pendientes de mayor peso (value desc; empate por número de ítem)
  const next = items
    .filter((i) => i.status !== "Aprobado")
    .sort((a, b) => b.value - a.value || compareSequence(a.sequence, b.sequence))
    .slice(0, 5);

  // --- Historial ---
  const history = arr(d.history).map((h) => ({
    month: h.month,
    label: monthLabel(h.month),
    longLabel: monthLabel(h.month, true),
    approvedScore: num(h.approved_score),
    pct: num(h.percentage),
    status: semaphore(h.percentage),
  }));

  // --- Accidentalidad (solo GRUPO2 / GRUPO3 con datos) ---
  const goal = (v) => (isNil(v) ? null : num(v));
  const accident = {
    show: (d.clasification === "GRUPO2" || d.clasification === "GRUPO3") && !isNil(d.accident_year),
    year: d.accident_year,
    count: num(d.accident_count),
    days: num(d.accident_days),
    countGoal: goal(d.accident_count_goal),
    daysGoal: goal(d.accident_days_goal),
    frequencyIndex: d.accident_frequency_index,
    severityIndex: d.accident_severity_index,
  };

  // --- Complementarios (grupo 3 no tiene; sin clasificar no aplica) ---
  const compTotal = num(d.complementary_items);
  const complementary = {
    show: classified && compTotal > 0,
    total: compTotal,
    approved: num(d.complementary_approved_items),
    pct: !isNil(d.complementary_percentage) ? num(d.complementary_percentage) : pctOf(d.complementary_approved_items, compTotal),
  };

  const model = {
    ok: !!raw,
    company: d.companie_name || "",
    classified,
    classificationId,
    classificationShort: group ? group.short : "Sin clasificar",
    classificationLabel: group ? group.long : "Empresa sin clasificar",
    total,
    phva,
    hasPhva: phva.length > 0,
    standards,
    hasStandards: standards.length > 0,
    items,
    hasItems: items.length > 0,
    breakdownError: d.breakdown_error === true,
    breakdownErrorId: d.breakdown_error_id || null,
    complementaryItems,
    statusCounts,
    next,
    history,
    hasHistory: history.length >= 2,
    accident,
    complementary,
  };
  model.finding = buildFinding(model);
  model.trend = buildTrend(model);
  model.issues = reconcile(model, d);
  return model;
}

// ---------- hallazgo: una frase que sale de los datos (spec 02) ----------
export function buildFinding(m) {
  if (!m.classified) {
    return "Tu empresa aún no está clasificada: sin grupo de estándares no hay una norma contra la cual medir el cumplimiento.";
  }
  const t = m.total;
  const x = fmtPct(t.pct);
  const top = m.next[0] || null;
  const base = t.requiredScore > 0 ? ` de ${fmtNum(t.requiredScore)}` : "";
  const s = t.status && t.status.key;

  if (s === "good") {
    return m.hasItems
      ? `Estás en ${x}. Mantén los ${m.statusCounts.Aprobado} ítems aprobados al día.`
      : `Estás en ${x}.`;
  }
  const target = s === "critical" ? THRESHOLDS.critical : THRESHOLDS.acceptable;
  const targetName = s === "critical" ? "moderadamente aceptable" : "aceptable";
  const gap = Math.round(((target / 100) * t.requiredScore - t.approvedScore) * 100) / 100;
  const gapText =
    gap > 0
      ? `Te faltan ${fmtNum(gap)} puntos${base} para “${targetName}” (${target} %).`
      : `Debes superar el ${target} % para llegar a “${targetName}”.`;
  let tail = "";
  if (top) {
    tail =
      s === "critical"
        ? ` Prioriza ${top.sequence} — ${top.description} (+${fmtNum(top.value)} pts).`
        : ` El ítem pendiente de mayor peso es ${top.sequence} (+${fmtNum(top.value)} pts).`;
  }
  return `Estás en ${x}. ${gapText}${tail}`;
}

// Cambio del último trimestre, con su base nombrada (puntos porcentuales del % ponderado)
function buildTrend(m) {
  const h = m.history;
  if (h.length < 4) return null;
  const last = h[h.length - 1];
  const prev = h[h.length - 4];
  const delta = Math.round((last.pct - prev.pct) * 100) / 100;
  return { delta, from: prev.longLabel, fromShort: prev.label, to: last.label };
}

// ---------- reconciliación (AC-2.2) ----------
function reconcile(m, d) {
  const issues = [];
  const check = (cond, msg) => {
    if (!cond) {
      issues.push(msg);
      // eslint-disable-next-line no-console
      console.assert(false, `[dashboardModel] ${msg}`);
    }
  };
  const close = (a, b, tol = 0.011) => Math.abs(a - b) <= tol;
  if (!m.classified) return issues;

  if (m.hasPhva) {
    const sum = m.phva.reduce((s, p) => s + p.approvedScore, 0);
    check(close(sum, m.total.approvedScore), `Σ phva.approved_score (${sum}) ≠ approved_score (${m.total.approvedScore})`);
    const req = m.phva.reduce((s, p) => s + p.requiredScore, 0);
    check(close(req, m.total.requiredScore), `Σ phva.required_score (${req}) ≠ required_score (${m.total.requiredScore})`);
    const ri = m.phva.reduce((s, p) => s + p.requiredItems, 0);
    check(ri === m.total.requiredItems, `Σ phva.required_items (${ri}) ≠ required_items (${m.total.requiredItems})`);
  }
  if (m.hasStandards) {
    const sum = m.standards.reduce((s, p) => s + p.approvedScore, 0);
    check(close(sum, m.total.approvedScore), `Σ standards.approved_score (${sum}) ≠ approved_score (${m.total.approvedScore})`);
  }
  if (m.hasItems) {
    check(m.items.length === m.total.requiredItems, `ítems requeridos del mosaico (${m.items.length}) ≠ required_items (${m.total.requiredItems})`);
    check(m.statusCounts.Aprobado === m.total.approvedItems, `ítems aprobados (${m.statusCounts.Aprobado}) ≠ approved_items (${m.total.approvedItems})`);
    const sumApproved = m.items.filter((i) => i.status === "Aprobado").reduce((s, i) => s + i.value, 0);
    check(close(sumApproved, m.total.approvedScore), `Σ value aprobados (${sumApproved}) ≠ approved_score (${m.total.approvedScore})`);
  }
  if (m.hasHistory && !isNil(d.progress_percentage)) {
    const last = m.history[m.history.length - 1];
    check(close(last.pct, m.total.pct), `último mes del historial (${last.pct}) ≠ progress_percentage (${m.total.pct})`);
  }
  return issues;
}
